import { ROUTE_PATHS } from '@constants/routes';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { buildFolderPath, buildProjectPath, FOLDER_BASE, parseProjectLocation } from '@lib/routing/projectPath';
import { isFolder, useGetFolderByPathQuery } from '@store/api/folderEndpoints';
import { useGetProjectQuery } from '@store/api/projectEndpoints';
import { resolveLabel, useGetPoiTypesQuery } from '@store/api/poiTypeEndpoints';
import { useGetReportDesignQuery } from '@store/api/reportDesignEndpoints';
import { useGetTeamsQuery } from '@store/api/teamEndpoints';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { validate as uuidValidate } from 'uuid';
import NavbarBreadcrumbs from './Breadcrumbs';

export interface BreadcrumbItem {
  label: string;
  link?: string;
}

export interface DynamicBreadcrumbsProps {
  breadcrumbItems?: BreadcrumbItem[];
}

const pathKeys = [
  'Folders', 'Projects', 'Inspections', 'Checklists', 'Poi', 'Steps', 'Users', 'Server Config', 'Settings', 'Teams'
];

const DynamicBreadcrumbs: React.FC<DynamicBreadcrumbsProps> = ({ breadcrumbItems }) => {
  const location = useLocation();
  const { t } = useLocalization();
  const segments = location.pathname.split('/').filter((x) => x);
  const isTeamDetailPath = location.pathname.startsWith('/teams/');
  const isPoiTypePath = location.pathname.startsWith('/customizations/poi-types');
  const isAppSettingsPath = location.pathname.startsWith(ROUTE_PATHS.CUSTOMIZATIONS_APP_SETTINGS);
  const isReportDesignsPath = location.pathname.startsWith(ROUTE_PATHS.CUSTOMIZATIONS_REPORT_DESIGNS);
  const isReportBrandingPath = location.pathname.startsWith(ROUTE_PATHS.CUSTOMIZATIONS_REPORT_BRANDING);
  const isRecentInspectionsPath = location.pathname.startsWith(ROUTE_PATHS.RECENT_INSPECTIONS);
  const isPoiTypeDetailPath = isPoiTypePath && segments.length > 2;
  const reportDesignId = isReportDesignsPath ? segments[2] : undefined;

  // A project is addressed as the leaf of its folder path, so the folder crumbs are the
  // same walk in both cases and only the project's own crumb needs its name looked up.
  const projectLocation = parseProjectLocation(location.pathname);
  const isFoldersPath = projectLocation?.base === FOLDER_BASE;

  // TeamDetail already fetches this on the same route; the subscription just reads the cache.
  const { data: teams } = useGetTeamsQuery(undefined, { skip: !isTeamDetailPath });
  // Same again: PoiTypeDetail holds this list, so resolving the id costs no request.
  const { data: poiTypes } = useGetPoiTypesQuery(undefined, { skip: !isPoiTypeDetailPath });
  // And again: the editor reads this design, so its name comes from the cache. It is the
  // stored name, not the one an unsaved draft holds.
  const { currentData: reportDesign } = useGetReportDesignQuery(reportDesignId ?? '', { skip: !reportDesignId });
  // A POI type's name is a locale map, so rendering it needs the fallback locale.
  const customizationLocales = useCustomizationLocales();
  // Likewise ProjectOverview: this resolves from cache rather than costing a request.
  const { data: project } = useGetProjectQuery(
    { id: projectLocation?.projectId ?? '', showError: false },
    { skip: !projectLocation?.projectId }
  );

  // Every entry point the user has — own team roots plus subtrees other teams shared with
  // them — is listed at the virtual root, so a path under one of them is reachable and
  // anything else is not. This is plain path arithmetic, not a permission decision.
  const { data: virtualRoot } = useGetFolderByPathQuery('/', { skip: !isFoldersPath });
  const reachableRoots = useMemo(
    () => virtualRoot?.children.filter(isFolder).map((child) => child.item.path) ?? [],
    [virtualRoot]
  );

  const localize = (label: string): string => {
    const matchedLocalizedLabel = pathKeys.find(pathKey => pathKey == label);

    return matchedLocalizedLabel ? t(`Common_${matchedLocalizedLabel.replace(/\s+/g, '').trim()}`) : label;
  };

  const folderTreeBreadcrumbItems = (): BreadcrumbItem[] => {
    const { folderPath, projectId } = projectLocation!;
    const folderSegments = folderPath.split('/').filter(Boolean);

    const items: BreadcrumbItem[] = [{ label: localize('Folders'), link: FOLDER_BASE }];

    folderSegments.forEach((segment, index) => {
      const walkedPath = `/${folderSegments.slice(0, index + 1).join('/')}`;
      // Inside a cross-team shared subtree the ancestors above the shared folder are
      // invisible to the user — render those crumbs without a link.
      const isReachable = reachableRoots.some(
        (rootPath) => walkedPath === rootPath || walkedPath.startsWith(`${rootPath}/`)
      );

      items.push({ label: segment, link: isReachable ? buildFolderPath(walkedPath) : undefined });
    });

    if (projectId) {
      items.push({
        label: project?.projectInfo.name ?? projectId,
        link: buildProjectPath({ folderPath, projectId }),
      });

      // Whatever follows the project — tab, inspection, poi, step — keeps the generic
      // treatment: known words are localized, ids are shown as they are.
      const tailStart = location.pathname.indexOf(projectId) + projectId.length;
      const tail = location.pathname.slice(tailStart).split('/').filter(Boolean);

      tail.forEach((segment, index) => {
        const label = uuidValidate(segment)
          ? segment
          : segment.charAt(0).toUpperCase() + segment.slice(1);

        items.push({
          label: localize(label),
          link: `${location.pathname.slice(0, tailStart)}/${tail.slice(0, index + 1).join('/')}`,
        });
      });
    }

    return items;
  };

  const genericBreadcrumbItems = (): BreadcrumbItem[] => segments.map((value, index) => {
    let label = index == 0 || !uuidValidate(value)
      ? value.split('-').map(labelPart => labelPart.charAt(0).toUpperCase() + labelPart.slice(1)).join(' ')
      : decodeURIComponent(value);

    // On /teams/:teamId show the team's name instead of its id.
    if (isTeamDetailPath && index === 1) {
      const teamName = teams?.find((team) => team._id === value)?.name;
      if (teamName) label = teamName;
    }

    // One fixed segment, and its words are already a phrase the CMS holds — otherwise the
    // crumb renders the English route name in every language.
    if (isRecentInspectionsPath) label = t('Statistic_RecentInspections');

    // The customization section has no folder-style path to walk, so its two fixed
    // segments are labelled from their own keys — otherwise they render as the raw
    // English words — and the trailing id resolves to the type's name.
    if (isPoiTypePath) {
      if (index === 0) label = t('Customization_Section');
      if (index === 1) label = t('Customization_CustomPoi');

      if (index === 2) {
        const poiType = poiTypes?.find((candidate) => candidate._id === value);
        if (poiType) label = resolveLabel(poiType.name, customizationLocales);
      }
    }

    if (isAppSettingsPath) {
      if (index === 0) label = t('Customization_Section');
      if (index === 1) label = t('AppSettings_Title');
    }

    if (isReportDesignsPath) {
      if (index === 0) label = t('Customization_Section');
      if (index === 1) label = t('ReportDesign_Title');
      // A design's name is plain text, unlike a POI type's.
      if (index === 2 && reportDesign) label = reportDesign.name;
    }

    if (isReportBrandingPath) {
      if (index === 0) label = t('Customization_Section');
      if (index === 1) label = t('ReportBranding_Title');
    }

    return {
      label: localize(label),
      link: `/${location.pathname.split('/').slice(1, index + 2).join('/')}`,
    };
  });

  const dynamicBreadcrumbItems: BreadcrumbItem[] = [
    { label: t('Common_Home'), link: '/' },
    ...(isFoldersPath ? folderTreeBreadcrumbItems() : genericBreadcrumbItems()),
  ];

  const itemsToRender = breadcrumbItems || dynamicBreadcrumbItems;

  return <NavbarBreadcrumbs items={itemsToRender} />;
};

export default DynamicBreadcrumbs;
